export const JLPT_LEVELS = ["5", "4", "3", "2", "1"] as const;

export type JlptLevel = typeof JLPT_LEVELS[number];

function isJlptLevel(value: unknown): value is JlptLevel {
    return typeof value === "string" && JLPT_LEVELS.includes(value as JlptLevel);
}

// This guard deliberately lives below the UI. A stale tab or a malformed
// cloud row must not be able to persist an invalid study-level combination.
export function normaliseJlptLevels(value: unknown): JlptLevel[] {
    if (!Array.isArray(value)) return ["5"];
    const levels = Array.from(new Set(value.filter(isJlptLevel))).sort((a, b) => Number(b) - Number(a));
    if (levels.length === 1) return levels;
    if (levels.length === 2 && Math.abs(Number(levels[0]) - Number(levels[1])) === 1) return levels;
    return ["5"];
}

export function formatJlptLevels(levels: readonly JlptLevel[]): string {
    return levels.map((level) => `N${level}`).join(" + ");
}

export function canSelectJlptLevel(levels: readonly JlptLevel[], level: JlptLevel): boolean {
    if (levels.includes(level)) return levels.length > 1;
    return levels.length === 1 && Math.abs(Number(levels[0]) - Number(level)) === 1;
}

export function toggleJlptLevel(levels: readonly JlptLevel[], level: JlptLevel): JlptLevel[] {
    const validLevels = normaliseJlptLevels(levels);
    if (validLevels.includes(level)) {
        return validLevels.length === 1 ? validLevels : validLevels.filter((selected) => selected !== level);
    }
    if (!canSelectJlptLevel(validLevels, level)) return validLevels;
    return normaliseJlptLevels([...validLevels, level]);
}
